import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_USER_FILTER_OPTIONS } from "../../../graphql/user";
import type { FilterOption } from "@outpave/ds";
import { surnameFirstName } from "utils/fullName";

/**
 * Cardholder names for the filter bar.
 *
 * The DS SelectFilter takes a flat option list and does not load as you type,
 * so the whole list has to be in hand. This fetches one large page rather than
 * paging: a company past this many users would lose the tail of the alphabet
 * from the filter, and the fix then is a searching filter in the DS, not a
 * bigger number here.
 */
const CARDHOLDER_LIMIT = 200;

interface UserNode {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
}

export const useCardholderOptions = ({
  skip = false,
}: { skip?: boolean } = {}): FilterOption[] => {
  // GET_USERS pulls company, department, cards and address for every row; this
  // reads three fields, so it takes the document that selects three fields.
  const { data } = useQuery(GET_USER_FILTER_OPTIONS, {
    variables: { first: CARDHOLDER_LIMIT },
    fetchPolicy: "cache-first",
    skip,
  });

  return useMemo(() => {
    const edges: { node: UserNode }[] = data?.users?.edges ?? [];
    return edges
      .map(({ node }) => ({
        value: node.id,
        label: surnameFirstName(node),
      }))
      .filter((option) => option.label.length > 0)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);
};
